// Vendor
import { inject, observer } from 'mobx-react';
import { SyntheticEvent } from 'react';
import * as React from 'react';

// Bernie
import { AnalyticsStore, ContextStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

// Stores
import { MultiItemStore } from 'stores/multi-item-store';
import { MultiItemStoreStatus } from 'stores/multi-item-store-util';
import { SearchCriteriaContext } from 'stores/contexts';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

// Models
import { InCartProperty, PrepareCheckoutResponse } from 'models/multi-item/multi-item-types';

// UITK
import { UitkPageLoader } from 'uitk-react-loader';
import { UitkToast } from 'uitk-react-toast';

// Components
import { HoldingAreaContent } from 'components/multi-item/holding-area-content';
import { ItemsPlayback } from 'components/multi-item/items-playback';

export interface MultiItemProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  multiItem?: MultiItemStore;
  routeStore?: RouteStore;
}

@withLocalization
@inject('multiItem', 'context', 'routeStore', 'analytics')
@observer
export class MultiItem extends React.Component<LocalizedComponentProps & MultiItemProps> {
  private toggleDialogRef;
  constructor(props) {
    super(props);

    this.cartItemDeleter = this.cartItemDeleter.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.getCartItems = this.getCartItems.bind(this);
    this.goToCheckout = this.goToCheckout.bind(this);
    this.onToastDismiss = this.onToastDismiss.bind(this);
    this.trackLoad = this.trackLoad.bind(this);
    this.toggleDialogRef = React.createRef();
    this.trackLoad();
    this.selectTitleText = this.selectTitleText.bind(this);
  }

  private trackLoad() {
    const { analytics, multiItem } = this.props;

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(
        `MultiProp.PlaybackLoad.${multiItem && multiItem.itemCount}`,
        'Playback component load',
        analytics.omnitureData,
        null,
        true
      );
    }
  }

  public componentDidMount() {
    this.getCartItems();
  }

  public componentDidUpdate() {
    const { multiItem, routeStore } = this.props;
    if (multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY_SUCCESS) {
      if (routeStore && routeStore.queryParams && routeStore.queryParams.pwaDialogNested) {
        routeStore.goBack();
        // Give some time for the nested dialog to go away
        setTimeout(() => {
          this.toggleDialogRef.click();
        }, 10);
      } else {
        this.toggleDialogRef.click();
      }
      multiItem.resetStatus();
    }
  }

  public closeDialog() {
    const { routeStore } = this.props;

    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
  }

  public goToCheckout() {
    const { l10n, multiItem, context, analytics } = this.props;
    let prepareCheckout = multiItem.prepareCheckoutByCartId.bind(multiItem);
    if (multiItem.cartContainsUnavailItems()) {
      const confirmationResponse = confirm(l10n.formatText('multiProperty.playback.propertiesInTripUnavailable'));
      if (confirmationResponse === true) {
        prepareCheckout = multiItem.removeUnavailableAndPrepareCheckout.bind(multiItem);
      } else {
        return;
      }
    }

    return prepareCheckout(context, analytics)
      .then((prepareCheckoutResponse: PrepareCheckoutResponse) => {
        const checkoutUrl = prepareCheckoutResponse && prepareCheckoutResponse.checkoutUrl;
        if (checkoutUrl && window && window.location) {
          window.location.href = checkoutUrl;
        }

        analytics.trackEvent(
          `MultiProp.Checkout.${multiItem.cartInfo.type}.${multiItem.itemCount}`,
          'number of properties in cart and business model',
          analytics.omnitureData,
          null,
          true
        );
      })
      .catch(() => {
        if (analytics) {
          analytics.trackEvent(
            `MultiProp.Checkout.Failure.${multiItem.itemCount}`,
            'Prepare checkout failure',
            analytics.omnitureData,
            null,
            true
          );
        }
      });
  }

  public getCartItems(): Promise<InCartProperty[]> {
    const { multiItem, context } = this.props;

    return multiItem.getCartDetails(context);
  }

  public cartItemDeleter(id) {
    const { multiItem, context, analytics } = this.props;

    return (e?: SyntheticEvent) => {
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }

      return multiItem.removeItemFromCart(context, id).then(response => {
        if (analytics) {
          analytics.trackEvent('MultiProp.RemoveProperty', 'MultiProp Property Removed', analytics.omnitureData, null, true);
        }
      });
    };
  }

  public onToastDismiss() {
    const { multiItem } = this.props;
    multiItem.resetStatus();
  }

  private selectTitleText(itemCount: number): string {
    const { multiItem } = this.props;

    if (itemCount === 1) {
      return multiItem && multiItem.showMixMatchExperience
        ? 'multiProperty.playback.mixAndMatch.room.titleSingle'
        : 'multiProperty.playback.titleSingle';
    }

    return multiItem && multiItem.showMixMatchExperience
      ? 'multiProperty.playback.mixAndMatch.room.titleMultiple'
      : 'multiProperty.playback.titleMultiple';
  }

  public render() {
    const { l10n, multiItem, routeStore } = this.props;
    if (!multiItem || multiItem.itemCount === 0) {
      return null;
    }

    const holdingAreaDialogQueryBase = { pwaDialog: 'multiItemDialog' };
    const dialogTo = {
      pathname: routeStore.pathname,
      search: routeStore.mergeQueryString && routeStore.mergeQueryString(routeStore.search, holdingAreaDialogQueryBase)
    };

    const toggleRef = toggleDialogRef => {
      this.toggleDialogRef = toggleDialogRef;
    };

    const titleText = l10n.formatText(this.selectTitleText(multiItem.itemCount), multiItem.itemCount);

    return (
      <React.Fragment>
        <ItemsPlayback setToggleRef={toggleRef} linkTo={dialogTo} itemCount={multiItem.itemCount} titleText={titleText} />
        <UitkPageLoader visible={multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY} />
        <SearchCriteriaContext.Consumer>
          {(searchCriteria: SearchCriteriaModel) => (
            <QueryRoute query={holdingAreaDialogQueryBase}>
              <Layer id="multi-item-layer">
                <HoldingAreaContent
                  cartItemDeleter={this.cartItemDeleter}
                  closeDialog={this.closeDialog}
                  getCartItems={this.getCartItems}
                  goToCheckout={this.goToCheckout}
                  l10n={l10n}
                  searchCriteria={searchCriteria}
                  isAllSoldOut={multiItem && multiItem.isAllSoldOut}
                  selectTitleText={this.selectTitleText}
                />
              </Layer>
            </QueryRoute>
          )}
        </SearchCriteriaContext.Consumer>
        <UitkToast onDismiss={this.onToastDismiss} show={multiItem && multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY_FAILURE}>
          {multiItem && multiItem.error && multiItem.error.message}
        </UitkToast>
      </React.Fragment>
    );
  }
}
